import React, { useLayoutEffect, useRef, useState } from 'react';
import { makeStyles, SvgIcon, TextField, Typography } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import FacebookIcon from '@material-ui/icons/Facebook';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { recordBlogShareOperation } from '../../../Apis/touzenBlogApi';

const useStyles = makeStyles((theme) => ({
  share: { alignSelf: 'stretch', minWidth: 0, margin: theme.spacing(0, 0, 1), textAlign: 'right' },
  actions: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '8px',
    borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`, borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
  },
  leading: { marginRight: 'auto', minWidth: 0, maxWidth: '100%' },
  shareButtons: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginLeft: 'auto' },
  action: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, flexShrink: 0,
    border: 0, borderRadius: 4, padding: 0, background: 'transparent', color: '#6F6F6F', textDecoration: 'none', cursor: 'pointer',
    '&:visited': { color: '#6F6F6F' }, '&:hover, &:focus': { color: theme.palette.primary.main },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    '&:disabled': { opacity: 0.5, cursor: 'default' },
  },
  icon: { fontSize: '20px' },
  message: { fontSize: '0.875rem', lineHeight: 1.7, overflowWrap: 'anywhere', margin: theme.spacing(1, 0) },
  fallback: { width: 360, maxWidth: '100%', marginTop: theme.spacing(1), '& .MuiInputBase-root': { fontSize: '1rem' }, '& .MuiInputLabel-root': { fontSize: '0.875rem' }, '& .MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(1)' } },
}));

export default function BlogShareActions({ slug, title, leadingAction = null }) {
  const classes = useStyles();
  const url = `${window.location.origin}/blog/posts/${encodeURIComponent(slug)}`;
  const [result, setResult] = useState({ url, kind: 'idle' });
  const [pending, setPending] = useState(false);
  const active = useRef(false);
  const generation = useRef(0);
  const sending = useRef(false);
  // Establish this URL's lifetime before a control can receive a click.
  useLayoutEffect(() => {
    active.current = true;
    generation.current += 1;
    sending.current = false;
    setPending(false);
    setResult({ url, kind: 'idle' });
    return () => { active.current = false; generation.current += 1; };
  }, [url]);
  const recordOperation = (channel) => {
    // Reporting must never delay or change the user's share/copy action.
    try { void Promise.resolve(recordBlogShareOperation(slug, channel)).catch(() => {}); } catch (error) { /* Best effort. */ }
  };
  const shareActivation = (channel, event) => {
    if ((event.type === 'click' && event.button === 0) || (event.type === 'auxclick' && event.button === 1)) recordOperation(channel);
  };
  const copy = async () => {
    if (sending.current) return;
    const requestGeneration = generation.current;
    const current = () => active.current && generation.current === requestGeneration;
    setResult({ url, kind: 'idle' });
    if (!navigator.clipboard?.writeText) { setResult({ url, kind: 'fallback' }); return; }
    sending.current = true;
    setPending(true);
    try {
      await navigator.clipboard.writeText(url);
      recordOperation('copy');
      if (current()) setResult({ url, kind: 'success' });
    } catch (error) { if (current()) setResult({ url, kind: 'fallback' }); }
    finally { if (current()) { sending.current = false; setPending(false); } }
  };
  const kind = result.url === url ? result.kind : 'idle';
  const xQuery = new URLSearchParams({ url, text: title });
  const facebookQuery = new URLSearchParams({ u: url });
  const mailQuery = `subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`;
  return <div className={classes.share}>
    <div className={classes.actions}>
      {leadingAction && <div className={classes.leading}>{leadingAction}</div>}
      <div role="group" aria-label="記事を共有" className={classes.shareButtons}>
      <a className={classes.action} href={`https://x.com/intent/tweet?${xQuery}`} target="_blank" rel="noopener noreferrer" aria-label="Xで共有" title="Xで共有"
        onClick={(event) => shareActivation('x', event)} onAuxClick={(event) => shareActivation('x', event)}>
        <SvgIcon className={classes.icon} aria-hidden="true" focusable="false"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.64 7.584H.47l8.6-9.835L0 1.154h7.594l5.243 6.932 6.064-6.933Zm-1.29 19.495h2.039L6.487 3.24H4.3l13.31 17.408Z" /></SvgIcon>
      </a>
      <a className={classes.action} href={`https://www.facebook.com/sharer/sharer.php?${facebookQuery}`} target="_blank" rel="noopener noreferrer" aria-label="Facebookで共有" title="Facebookで共有"
        onClick={(event) => shareActivation('facebook', event)} onAuxClick={(event) => shareActivation('facebook', event)}><FacebookIcon className={classes.icon} aria-hidden="true" focusable="false" /></a>
      <a className={classes.action} href={`mailto:?${mailQuery}`} aria-label="メールで共有" title="メールで共有"
        onClick={(event) => shareActivation('mail', event)} onAuxClick={(event) => shareActivation('mail', event)}><MailOutlineIcon className={classes.icon} aria-hidden="true" focusable="false" /></a>
      <button className={classes.action} type="button" onClick={copy} disabled={pending} aria-label="記事URLをコピー" title="記事URLをコピー"><FileCopyOutlinedIcon className={classes.icon} aria-hidden="true" focusable="false" /></button>
      </div>
    </div>
    {kind === 'success' && <Typography role="status" className={classes.message}>記事URLをコピーしました。</Typography>}
    {kind === 'fallback' && <>
      <Typography role="status" className={classes.message}>コピーできませんでした。URLを選択して手動でコピーしてください。</Typography>
      <TextField id={`blog-share-url-${encodeURIComponent(slug)}`} className={classes.fallback} label="共有する記事URL" variant="outlined" size="small" value={url}
        InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} onFocus={(event) => event.target.select()} />
    </>}
  </div>;
}
